import { useState } from 'react';

// The Style section shared by the editors of every Pulse embed that sits on a
// client's own website: contact forms and newsletter sign-up forms
// (server/embedStyle.js cleans what it saves). `kind` hides what a form doesn't
// have (newsletter forms have no enquiry pills).
// The form's look, as settings — never one client's hard-coded stylesheet.
// Blank colours use the light/dark theme's defaults.
const STYLE_COLOURS = [
  ['ink', 'Text'], ['muted', 'Hints and placeholders'], ['surface', 'Card background'], ['field', 'Field background'],
  ['fieldBorder', 'Field and pill borders'], ['cardBorder', 'Card border (blank = none)'], ['focus', 'Keyboard focus ring'],
  ['error', 'Error text'], ['success', 'Sent confirmation'],
];
const STYLE_PRESETS = [
  { label: 'Pulse default', brandColor: '', style: {} },
  // An editorial look: navy ink, white card with a fine rule, square-ish
  // corners, small uppercase labels, outlined pills with a tick, an uppercase
  // button with an arrow (first made for Kappa FuturFestival).
  { label: 'Outlined, uppercase labels', brandColor: '#0b2132', style: {
    ink: '#0b2132', muted: '#47576c', surface: '#ffffff', field: '#ffffff', fieldBorder: '#717c84', cardBorder: '#d5d7dc',
    focus: '#216e9c', error: '#a12d2b', success: '#205c46', radius: 4, fieldRadius: 2, padding: 28,
    labelStyle: 'caps', fieldSize: 'large', pillStyle: 'tick', buttonStyle: 'caps',
  } },
];
export default function StyleSettings({ style, onChange, onPreset, kind = 'contact' }) {
  const [more, setMore] = useState(false);
  const set = (k, v) => onChange({ ...style, [k]: v });
  const opt = (k, lbl, opts) => (
    <label style={{ ...label, fontWeight: 600, marginTop: 8 }}>{lbl}
      <select style={input} value={style[k] || opts[0][0]} onChange={(e) => set(k, e.target.value)}>
        {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
    </label>
  );
  const num = (k, lbl, max) => (
    <label style={{ ...chkRow, minHeight: 40, margin: '6px 0' }}>
      <span style={{ flex: '1 1 160px' }}>{lbl}</span>
      <input type="number" min={0} max={max} inputMode="numeric" placeholder="default" style={{ ...input, width: 100, flex: 'none' }} value={style[k] ?? ''}
        onChange={(e) => set(k, e.target.value === '' ? '' : Math.max(0, Math.min(max, Number(e.target.value) || 0)))} aria-label={lbl} />
      <span style={{ width: 24, color: 'var(--muted)' }}>px</span>
    </label>
  );
  return (
    <div style={subRow}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ ...hint, margin: 0, flex: '1 1 140px' }}>Start from a look, then adjust:</span>
        {STYLE_PRESETS.map((p) => <button key={p.label} type="button" style={miniBtn} onClick={() => onPreset(p)}>{p.label}</button>)}
      </div>
      {opt('labelStyle', 'Labels', [['normal', 'Normal'], ['caps', 'Small UPPERCASE']])}
      {opt('fieldSize', 'Fields', [['normal', 'Standard'], ['large', 'Large, more space']])}
      {kind === 'contact' && opt('pillStyle', 'Selected enquiry type', [['filled', 'Filled'], ['tick', 'Filled with a tick ✓']])}
      {opt('buttonStyle', kind === 'newsletter' ? 'Sign-up button' : 'Send button', [['normal', 'Standard'], ['caps', 'UPPERCASE with an arrow ↗']])}
      {num('radius', 'Card corners', 40)}
      {num('fieldRadius', 'Field and button corners', 30)}
      {num('padding', 'Space inside the card', 48)}
      <button type="button" style={{ ...miniBtn, marginTop: 6 }} onClick={() => setMore(!more)}>{more ? 'Hide colours' : 'Colours…'}</button>
      {more && STYLE_COLOURS.map(([k, lbl]) => (
        <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
          <input type="color" style={{ width: 44, height: 36, border: '1px solid var(--hairline)', borderRadius: 8, background: 'var(--bg)', padding: 2 }}
            value={/^#[0-9a-f]{6}$/i.test(style[k] || '') ? style[k] : '#ffffff'} onChange={(e) => set(k, e.target.value)} aria-label={lbl} />
          <span style={{ fontSize: 13, flex: '1 1 140px' }}>{lbl}</span>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{style[k] || 'default'}</span>
          {style[k] && <button type="button" style={{ ...miniBtn, minHeight: 32 }} onClick={() => set(k, '')}>Clear</button>}
        </div>
      ))}
      <details style={{ marginTop: 10 }}>
        <summary style={{ fontSize: 12.5, cursor: 'pointer', minHeight: 32 }}>Custom CSS (advanced)</summary>
        <textarea style={{ ...input, minHeight: 100, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12.5, marginTop: 6 }} value={style.customCss || ''} maxLength={10000}
          placeholder={'.pill { font-size: 13px; }'} onChange={(e) => set('customCss', e.target.value)} aria-label="Custom CSS" />
        <div style={{ ...hint, margin: '4px 0 0' }}>
          Applies to this form only, never the rest of the website. {kind === 'newsletter'
            ? <>Classes: <code>.wrap</code> (card), <code>.f</code> (field label), <code>.t</code> (fields), <code>.chk</code> (consent box), <code>button[type=submit]</code>, <code>.done</code> (thank-you), <code>.err</code>, <code>.notice</code>, <code>.modal</code> (popup).</>
            : <>Classes: <code>.wrap</code> (card), <code>label</code>, <code>.lbl</code> (pill group label), <code>.t</code> (fields), <code>.pill</code> / <code>.pill.on</code>, <code>button[type=submit]</code>, <code>.ok</code> (sent), <code>.err</code>, <code>.route</code>, <code>.notice</code>, <code>.modal</code> (popup).</>}
          {' '}Only https:// links are allowed in <code>url()</code>.
        </div>
      </details>
    </div>
  );
}

const hint = { fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 8px' };
const label = { display: 'block', fontSize: 12, fontWeight: 700, margin: '12px 0 4px' };
const chkRow = { display: 'flex', gap: 8, alignItems: 'flex-start', fontWeight: 400, fontSize: 13, lineHeight: 1.45, minHeight: 32 };
const input = { width: '100%', boxSizing: 'border-box', minHeight: 40, padding: '9px 10px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 14, fontFamily: 'inherit' };
const btn = { minHeight: 40, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'inherit', fontSize: 13, cursor: 'pointer', boxSizing: 'border-box' };
const miniBtn = { ...btn, minHeight: 36, padding: '6px 10px' };
const subRow = { border: '1px solid var(--hairline)', borderRadius: 9, padding: 8, marginTop: 6, background: 'var(--bg)' };
